import { useEffect, useMemo, useState } from "react";
import { Search, Stethoscope, Building2 } from "lucide-react";
import { api, eur, fmtDate } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Card, Empty, PageHead, StatCard } from "@/components/common";

const Tbl = ({ rows, testid }) => rows.length === 0 ? <Empty>Nessun elemento.</Empty> : (
  <Card className="overflow-x-auto">
    <Table data-testid={testid}>
      <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Città</TableHead><TableHead className="text-right">Ritiri totali</TableHead><TableHead className="text-right">Singole</TableHead><TableHead className="text-right">Collettive / smaltimenti</TableHead><TableHead>Ultimo ritiro</TableHead><TableHead className="text-right">Da incassare</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((v) => (
          <TableRow key={v.id} data-testid={`partner-row-${v.id}`}>
            <TableCell><p className="font-semibold text-[#0D3B52]">{v.clinic_name}</p><p className="text-xs text-[#5C6B73]">{v.piva ? `P.IVA ${v.piva}` : v.email}</p></TableCell>
            <TableCell className="text-sm">{v.city}</TableCell>
            <TableCell className="text-right font-display text-xl font-semibold text-[#0D3B52]">{v.pratiche_count}</TableCell>
            <TableCell className="text-right text-emerald-700 font-semibold">{v.singole}</TableCell>
            <TableCell className="text-right text-[#997316] font-semibold">{v.collettive}</TableCell>
            <TableCell className="whitespace-nowrap">{fmtDate(v.ultimo_ritiro)}</TableCell>
            <TableCell className={`text-right whitespace-nowrap font-semibold ${v.residuo > 0 ? "text-rose-700" : ""}`}>{eur(v.residuo)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Card>
);

export default function Partner() {
  const [vets, setVets] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/vets").then((r) => setVets(r.data)); }, []);
  const filtered = useMemo(() => vets.filter((v) => !q || [v.clinic_name, v.city, v.piva].some((x) => String(x || "").toLowerCase().includes(q.toLowerCase()))), [vets, q]);
  const veterinari = filtered.filter((v) => (v.tipo || "veterinario") !== "agenzia");
  const agenzie = filtered.filter((v) => v.tipo === "agenzia");
  const sum = (arr, k) => arr.reduce((a, v) => a + (v[k] || 0), 0);

  return (
    <div data-testid="admin-partner">
      <PageHead eyebrow="Partner" title="Veterinari e agenzie esterne" subtitle="Quanti ritiri passa ciascun partner, divisi tra cremazioni singole e collettive/smaltimenti. Il tipo (veterinario o agenzia) si imposta in Anagrafica." />
      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Veterinari" value={veterinari.length} icon={Stethoscope} testid="partner-vets-count" />
        <StatCard label="Ritiri da veterinari" value={sum(veterinari, "pratiche_count")} testid="partner-vets-ritiri" />
        <StatCard label="Agenzie esterne" value={agenzie.length} icon={Building2} tone="gold" testid="partner-agenzie-count" />
        <StatCard label="Ritiri da agenzie" value={sum(agenzie, "pratiche_count")} tone="gold" testid="partner-agenzie-ritiri" />
      </div>
      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3 top-3 text-[#5C6B73]" />
        <Input placeholder="Cerca nome, città, P.IVA…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="partner-search" />
      </div>
      <Tabs defaultValue="vet">
        <TabsList className="bg-white border border-[#E2DDD5]">
          <TabsTrigger value="vet" data-testid="partner-tab-vet" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Veterinari ({veterinari.length})</TabsTrigger>
          <TabsTrigger value="agenzie" data-testid="partner-tab-agenzie" className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">Agenzie esterne ({agenzie.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="vet" className="mt-6"><Tbl rows={veterinari} testid="partner-vet-table" /></TabsContent>
        <TabsContent value="agenzie" className="mt-6"><Tbl rows={agenzie} testid="partner-agenzie-table" /></TabsContent>
      </Tabs>
    </div>
  );
}
